// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyIcon from '@mui/icons-material/Key';
import api from '../../utils/api';
import { filterItems } from '../../utils/recherche';
import ChampRecherche from '../ChampRecherche';
import { isDummyAddress } from './commun';

/**
 * Onglet « Comptes famille » de la fiche d'un foyer : les proches qui ont accès
 * à l'application mobile, leur création et leur suppression. L'envoi d'un
 * nouveau mot de passe s'ouvre aussi depuis la barre d'actions de la page, qui
 * tient donc ce dialogue (`onSendPassword`).
 */
export default function RelativesTab({
  clientId, familyUsers, recherche, onRecherche, envoiMdpEnCours: passwordSendInProgress, onEnvoyerMotDePasse: onSendPassword,
  onConfirmation, onSnack, onErreur: onError, onRecharger: onReload,
}) {
  const [familyDialog, setFamilyDialog] = useState(false);
  const [familyForm, setFamilyForm] = useState({ email: '', full_name: '' });
  const [savingFamily, setSavingFamily] = useState(false);
  const [deleteFamilyTarget, setDeleteFamilyTarget] = useState(null);

  // Aucun mot de passe dans ce dialogue : le serveur le tire et l'envoie au
  // proche, et refuse en 400 un champ `password`. L'exploitant ne le connaît
  // jamais.
  const openFamilyDialog = () => {
    setFamilyForm({ email: '', full_name: '' });
    setFamilyDialog(true);
  };

  const handleCreateFamily = async () => {
    setSavingFamily(true);
    try {
      const res = await api.post('/mobile/users', {
        email: familyForm.email.trim().toLowerCase(),
        full_name: familyForm.full_name || null,
        client_id: clientId,
      });
      setFamilyDialog(false);
      onConfirmation(`Compte créé : mot de passe envoyé à ${res.data.email}.`);
      onReload();
    } catch (e) {
      onError(e.response?.data?.detail || 'Erreur création compte famille');
    } finally {
      setSavingFamily(false);
    }
  };

  const handleDeleteFamily = async () => {
    if (!deleteFamilyTarget) return;
    try {
      await api.delete(`/mobile/users/${deleteFamilyTarget.id}`);
      setDeleteFamilyTarget(null);
      onSnack('Compte famille supprimé');
      onReload();
    } catch (e) {
      onError(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  const filteredRelatives = useMemo(() => filterItems(familyUsers, recherche, (u) => [
    u.full_name, u.email,
  ]), [familyUsers, recherche]);

  return (
    <>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Comptes d'accès à l'application mobile Vigeos (alertes de chute, vidéo).
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <ChampRecherche
            valeur={recherche}
            onChange={onRecherche}
            placeholder="Rechercher un proche par nom ou adresse courriel"
            compte={filteredRelatives.length}
            total={familyUsers.length}
            nomObjet="compte"
          />
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openFamilyDialog}>
            Ajouter un proche
          </Button>
        </Box>
        {familyUsers.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            Aucun compte famille. Ajoutez un proche pour lui donner accès à l'application mobile.
          </Paper>
        ) : (
          <Table size="small" component={Paper}>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRelatives.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Aucun compte ne correspond à cette recherche.
                  </TableCell>
                </TableRow>
              ) : filteredRelatives.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell><Typography variant="body2" fontWeight="medium">{u.full_name || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{u.email}</Typography></TableCell>
                  <TableCell>
                    <Chip label={u.is_active ? 'Actif' : 'Inactif'} size="small"
                      color={u.is_active ? 'success' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={!u.is_active ? 'Compte désactivé'
                      : isDummyAddress(u.email) ? 'Adresse fictive : aucun courriel ne lui parviendrait'
                      : 'Envoyer un nouveau mot de passe à ce proche'}>
                      <span>
                        <IconButton size="small"
                          disabled={!u.is_active || isDummyAddress(u.email) || passwordSendInProgress}
                          onClick={() => onSendPassword(u)}>
                          <KeyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Supprimer ce compte">
                      <IconButton size="small" onClick={() => setDeleteFamilyTarget(u)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Dialog open={familyDialog} onClose={() => setFamilyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Ajouter un proche</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Email *" type="email" size="small" fullWidth value={familyForm.email}
            onChange={e => setFamilyForm(f => ({ ...f, email: e.target.value }))} autoFocus
            error={isDummyAddress(familyForm.email)}
            helperText={isDummyAddress(familyForm.email)
              ? "Adresse fictive : aucun courriel ne lui parviendrait. Un compte de démonstration se crée dans le conteneur backend (compte_demo_famille)."
              : ''} />
          <TextField label="Nom complet" size="small" fullWidth value={familyForm.full_name}
            onChange={e => setFamilyForm(f => ({ ...f, full_name: e.target.value }))} />
          <Typography variant="body2" color="text.secondary">
            Le proche reçoit son mot de passe par courriel, à cette adresse, et à personne
            d&apos;autre : il ne s&apos;affiche pas ici. Il pourra le changer dans l&apos;app.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFamilyDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleCreateFamily}
            disabled={!familyForm.email || isDummyAddress(familyForm.email) || savingFamily}>
            {savingFamily ? <CircularProgress size={16} /> : 'Créer le compte'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteFamilyTarget} onClose={() => setDeleteFamilyTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Supprimer le compte famille</DialogTitle>
        <DialogContent>
          <Typography>Révoquer l'accès de <strong>{deleteFamilyTarget?.full_name || deleteFamilyTarget?.email}</strong> ?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteFamilyTarget(null)}>Annuler</Button>
          <Button variant="contained" color="error" onClick={handleDeleteFamily}>Supprimer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
